import i18next from 'i18next'
import {
  HttpResult,
  loadingAsyncState,
  unloadedAsyncState,
} from 'widgets-for-react'
import {
  parseResponse,
  tryParseJsonResponse,
} from '../../../../utils/parseResponse'
import {
  TranslationApiData,
  getTranslation,
  translationValidator,
} from '../../../../utils/translation'
import { TypeCastingValidator, canSafelyCast } from '../../../../utils/safe-type-casting'
import { imageValidator } from '../../image/imageApi'
import { ImageData } from '../../image/imageState'
import { sponsorDataValidator } from '../../sponsor/sponsorApi'
import { SponsorData } from '../../sponsor/sponsorState'
import { initialComponentSaverState } from '../componentSaver/componentSaverState'
import { initialComponentTeasersState } from '../componentTeasers/componentTeasersState'
import { ComponentData, ComponentState } from './componentDetailState'

const updateLastViewedComponents = async (
  componentId: number
): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(
    `/api/v1/components/view/${componentId}?language=${i18next.language}`,
    {
      method: 'post',
      headers: headers,
    }
  )
  return parseResponse(res)
}

export const loadLastViewedComponentsUpdate = (componentId: number) =>
  loadingAsyncState(() => updateLastViewedComponents(componentId))

const getComponent = async (id: number): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(`/api/v1/components/${id}?language=${i18next.language}`, {
    method: 'get',
    headers: headers,
  })
  return tryParseJsonResponse(res)
}

type ComponentTranslationApiData = {
  MetaTitle: string
  Introduction: string
  Preparation: string
  ServingSuggestions: string
  Name: string
  Language: { Code: string }
  Brand: SponsorData
}

type ComponentApiData = {
  Id: number
  Allergens: { Title: string; Translation: TranslationApiData }[]
  ImageURL: ImageData
  Public: boolean
  Brand: SponsorData
  Ingredients: {
    Id: number
    Amount: string
    ItemOrder: number
    Prepreperations: { Id: number; Translation: TranslationApiData }[]
    Ingredient: { Id: number; Translation: TranslationApiData }
    UnitOfMeasurement: { Id: number; Translation: TranslationApiData }
  }[]
  MainIngredients: {
    Ingredient: { Id: number; Translation: TranslationApiData }
  }[]
  SubscriptionTypes: { Id: number }[]
  ComponentTranslations: ComponentTranslationApiData[]
  WorldFlavours: { Id: number; Translation: TranslationApiData }[]
  Techniques: { Id: number; Translation: TranslationApiData }[]
  Appearances: { Id: number; Translation: TranslationApiData }[]
  Textures: { Id: number; Translation: TranslationApiData }[]
  Draft: boolean
  UserId: string
  ParentComponentId: number
}

const componentDataValidator: TypeCastingValidator<ComponentApiData> = {
  kind: 'object',
  fieldsValidator: {
    Allergens: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Title: { kind: 'string' },
          Translation: translationValidator,
        },
      },
    },
    Id: { kind: 'number' },
    ImageURL: imageValidator,
    Ingredients: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Id: { kind: 'number' },
          ItemOrder: { kind: 'number' },
          Amount: { kind: 'string' },
          Ingredient: {
            kind: 'object',
            fieldsValidator: {
              Id: { kind: 'number' },
              Translation: translationValidator,
            },
          },
          Prepreperations: {
            kind: 'array',
            elementValidator: {
              kind: 'object',
              fieldsValidator: {
                Id: { kind: 'number' },
                Translation: translationValidator,
              },
            },
          },
          UnitOfMeasurement: {
            kind: 'object',
            fieldsValidator: {
              Id: { kind: 'number' },
              Translation: translationValidator,
            },
          },
        },
      },
    },
    MainIngredients: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Ingredient: {
            kind: 'object',
            fieldsValidator: {
              Id: { kind: 'number' },
              Translation: translationValidator,
            },
          },
        },
      },
    },
    Public: { kind: 'boolean' },
    Brand: sponsorDataValidator,
    SubscriptionTypes: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: { Id: { kind: 'number' } },
      },
    },
    ComponentTranslations: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          MetaTitle: { kind: 'string' },
          Introduction: { kind: 'string' },
          Preparation: { kind: 'string' },
          ServingSuggestions: { kind: 'string' },
          Name: { kind: 'string' },
          Language: {
            kind: 'object',
            fieldsValidator: { Code: { kind: 'string' } },
          },
          Brand: sponsorDataValidator,
        },
      },
    },
    WorldFlavours: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Translation: translationValidator,
          Id: { kind: 'number' },
        },
      },
    },
    Techniques: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Translation: translationValidator,
          Id: { kind: 'number' },
        },
      },
    },
    Appearances: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Translation: translationValidator,
          Id: { kind: 'number' },
        },
      },
    },
    Textures: {
      kind: 'array',
      elementValidator: {
        kind: 'object',
        fieldsValidator: {
          Translation: translationValidator,
          Id: { kind: 'number' },
        },
      },
    },
    Draft: { kind: 'boolean' },
    UserId: { kind: 'string' },
    ParentComponentId: { kind: 'number' },
  },
}

export const getParsedComponentData = async (
  componentId: number
): Promise<HttpResult<ComponentData>> =>
  getComponent(componentId).then((result) => {
    const emptyBrand = {
      Brand: '',
      UrlField: { Title: '', Url: '' },
      UrlLink: { Title: '', Url: '' },
      EnglishBrandPageUrl: { Title: '', Url: '' }
    };

    if (result.kind === 'result') {
      const preprocessedValue = {
        ...result.value,
        // UnitOfMeasurement, Ingredient, ComponentTranslation brand and Brand might be null, so we need to add dummy values in order to safely cast it to the componentData type later
        Brand:
        // Brand may already exist, but the brand may not have an english url (this was added later in GSX-16), so we create a dummy for that as well if it was null
          result.value.Brand === null ? emptyBrand : result.value.Brand.EnglishBrandPageUrl === null ? { ...result.value.Brand, EnglishBrandPageUrl: { Title: '', Url: '' }} : result.value.Brand,
        ComponentTranslations: result.value.ComponentTranslations.map((componentTranslation: ComponentTranslationApiData) => ({
          ...componentTranslation,
          // Brand may already exist, but the brand may not have an english url (this was added later in GSX-16), so we create a dummy for that as well if it was null
          Brand: componentTranslation.Brand === null ? emptyBrand : componentTranslation.Brand.EnglishBrandPageUrl === null ? { ...componentTranslation.Brand, EnglishBrandPageUrl: { Title: '', Url: '' }} : componentTranslation.Brand,
        })),
        Ingredients: result.value.Ingredients.map((ingredient: any) => ({
          ...ingredient,
          Ingredient:
            ingredient.Ingredient === null
              ? { Id: 0, Translation: [] }
              : {
                  ...ingredient.Ingredient,
                  Translation:
                    ingredient.Ingredient.Translation === null
                      ? []
                      : ingredient.Ingredient.Translation,
                },
          UnitOfMeasurement:
            ingredient.UnitOfMeasurement === null
              ? { Id: 0, Translation: [] }
              : ingredient.UnitOfMeasurement,
        })),
        // Draft, UserId and ParentComponentId can be null when deploying the shadow component changes as they are new fields in ES
        Draft: result.value.Draft === null ? false : result.value.Draft,
        UserId: result.value.UserId === null ? '' : result.value.UserId,
        ParentComponentId:
          result.value.ParentComponentId === null
            ? 0
            : result.value.ParentComponentId,
      }
      const maybeComponentData = canSafelyCast<ComponentApiData>(
        componentDataValidator,
        preprocessedValue
      )
      if (maybeComponentData.kind === 'r') {
        const {
          ComponentTranslations,
          Allergens,
          Ingredients,
          MainIngredients,
          WorldFlavours,
          Techniques,
          Appearances,
          Textures,
          Public,
          SubscriptionTypes,
          ImageURL,
          Brand,
          Id,
          Draft,
          UserId,
          ParentComponentId,
        } = maybeComponentData.v
        const componentTranslation = ComponentTranslations.find(
          (translation) => translation.Language.Code === i18next.language
        )
        if (componentTranslation !== undefined) {
          const {
            MetaTitle,
            Name,
            Introduction,
            Preparation,
            ServingSuggestions,
          } = componentTranslation
          Ingredients.sort((fst, snd) => fst.ItemOrder - snd.ItemOrder)

          // Since componentTranslation.Brand is replaced with a dummy object to satisfy the validator,
          // We need to check for a valid brand name instead of a valid (but potentially empty) brand object
          // Otherwise, fall back to the component brand on the top level component
          const selectedLanguageBrand = componentTranslation.Brand.Brand && componentTranslation.Brand.UrlField ? componentTranslation.Brand : Brand
          
          const componentData: ComponentData = {
            Name,
            Introduction,
            Preparation,
            ServingSuggestions,
            Public,
            SubscriptionTypes,
            ImageURL,
            Brand: selectedLanguageBrand,
            Id,
            Draft,
            UserId,
            ParentComponentId,
            allergens: Allergens.map((allergen) => ({
              title: allergen.Title,
              translation: getTranslation(allergen.Translation),
            })),
            mainIngredients: MainIngredients.map((mainIngredient) => ({
              id: mainIngredient.Ingredient.Id,
              translation: getTranslation(
                mainIngredient.Ingredient.Translation
              ),
            })),
            ingredients: Ingredients.map((ingredient) => ({
              id: ingredient.Id,
              amount: ingredient.Amount,
              prepreparations: ingredient.Prepreperations.map((p) => ({
                id: p.Id,
                translation: getTranslation(p.Translation),
              })),
              unitOfMeasurement: {
                id: ingredient.UnitOfMeasurement.Id,
                translation: getTranslation(
                  ingredient.UnitOfMeasurement.Translation
                ),
              },
              ingredient: {
                id: ingredient.Ingredient.Id,
                translation: getTranslation(ingredient.Ingredient.Translation),
              },
            })),
            techniques: Techniques.map((technique) => ({
              id: technique.Id,
              translation: getTranslation(technique.Translation),
            })),
            appearances: Appearances.map((appearance) => ({
              id: appearance.Id,
              translation: getTranslation(appearance.Translation),
            })),
            textures: Textures.map((texture) => ({
              id: texture.Id,
              translation: getTranslation(texture.Translation),
            })),
            worldFlavours: WorldFlavours.map((worldFlavour) => ({
              id: worldFlavour.Id,
              translation: getTranslation(worldFlavour.Translation),
            })),
          }
          return {
            kind: 'result',
            status: result.status,
            value: componentData,
          }
        }
      }
      return { kind: 'failed', status: result.status }
    }
    return result
  })

export const loadComponentDataState = (componentId: number) =>
  loadingAsyncState<ComponentState>(() =>
    getParsedComponentData(componentId).then((result) => {
      if (result.kind === 'result') {
        const componentData = result.value
        const { Id, ParentComponentId } = componentData
        return {
          kind: 'result',
          status: result.status,
          value: {
            componentData,
            parentComponentNameState: ParentComponentId
              ? loadComponentName(ParentComponentId)
              : unloadedAsyncState<string>(),
            comparableComponentsState: initialComponentTeasersState(() =>
              getComparableComponents(Id)
            ),
            lastViewedComponentUpdateState: loadLastViewedComponentsUpdate(Id),
            componentSaverState: initialComponentSaverState,
            showLoggedOutEditComponentNotification: false,
          },
        }
      }
      return result
    })
  )

const getComponentName = async (
  componentId: number
): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(
    `/api/v1/components/name/${componentId}?language=${i18next.language}`,
    {
      headers: headers,
    }
  )
  return tryParseJsonResponse(res)
}

type NameResponse = { Name: string }
const nameResponseValidator: TypeCastingValidator<NameResponse> = {
  kind: 'object',
  fieldsValidator: { Name: { kind: 'string' } },
}

const loadComponentName = (componentId: number) =>
  loadingAsyncState(() =>
    getComponentName(componentId).then((result) => {
      if (result.kind === 'result') {
        const maybeNameResponse = canSafelyCast(
          nameResponseValidator,
          result.value
        )
        return maybeNameResponse.kind === 'r'
          ? {
              kind: 'result',
              status: result.status,
              value: maybeNameResponse.v.Name,
            }
          : { kind: 'failed', status: result.status }
      }
      return result
    })
  )

async function getComparableComponents(componentId: number): Promise<any> {
  const response = await fetch(
    `/api/v1/components/comparable?componentId=${componentId}&language=${i18next.language}`
  )
  if (!response.ok) throw Error(response.statusText)
  return await response.json()
}
